import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BaseView, BaseText } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { AppIcons } from '@/components/AppIcons'
import {
  kemCardActions,
  kemCardManageActions,
  KemCardActionTypes,
  KemCardActionsType,
  addToWalletActions,
} from '@/constants/kemCardActions'
import { CardInfoFromWallet } from '@/domain/wallet/walletTypes'
import { useTheme } from '@/hooks/useTheme'
import { responsiveAdapter } from '@/utils'
import { Modal } from '@/components/Modal'

interface ActionsKemCardProps {
  isLocked?: boolean
  lockCard: () => void
  unlockCard: () => void
  isLoading?: boolean
  isLoadingBlockAndUnblock?: boolean
  cardInfo?: CardInfoFromWallet
}

export const ActionsKemCard: React.FC<ActionsKemCardProps> = ({
  isLocked = false,
  lockCard,
  unlockCard,
  isLoading,
  isLoadingBlockAndUnblock,
}) => {
  const [isManageCard, setIsManageCard] = useState(false)
  const [isAddToWallet, setIsAddToWallet] = useState(false)
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()

  const manageCard = () => {
    setIsManageCard(true)
  }

  const addToWallet = () => {
    setIsAddToWallet(true)
  }

  // const redirectToChooseStyleScreen = () => {
  //   navigate('/kem-card/choose-style', {
  //     state: { isChangeCardStyle: true },
  //   })
  // }

  const redirectToKemCardInfoScreen = () => {
    navigate('/kem-card/card-info')
  }

  const redirectToWalletInstructionsScreen = (platform: 'apple' | 'google') => {
    // Only pass platform - card details will be fetched securely from wallet state
    navigate('/kem-card/wallet-instructions', {
      state: { platform },
    })
  }

  const handleLockCard = () => {
    setIsManageCard(false)
    lockCard()
  }

  const handleActions = (action: KemCardActionTypes) => {
    switch (action) {
      case 'lockCard':
        return handleLockCard()

      case 'unlockCard':
        return unlockCard()

      // case 'changeStyle':
      //   return redirectToChooseStyleScreen()

      case 'cardInfo':
        return redirectToKemCardInfoScreen()

      case 'manageCard':
        return manageCard()

      case 'appleWallet':
        return redirectToWalletInstructionsScreen('apple')

      case 'googleWallet':
        return redirectToWalletInstructionsScreen('google')

      case 'addToWallet':
        return addToWallet()

      default:
        return
    }
  }

  const isAndroid = navigator.userAgent.toLowerCase().includes('android')
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

  const actions = (actionsList: KemCardActionsType[]) => {
    return actionsList.map(
      ({ translationKey, action, iconName, parentAction }) => {
        const disabledActionsWhenLocked: KemCardActionTypes[] = [
          'appleWallet',
          // 'changeStyle',
        ]

        const isLoadingLockAndUnlock =
          isLoadingBlockAndUnblock &&
          (action === 'lockCard' || action === 'unlockCard')
        const isDisabled =
          isLocked && disabledActionsWhenLocked.includes(action)

        // Platform-specific filtering
        if (
          (isAndroid && action === 'appleWallet') ||
          (!isAndroid && action === 'googleWallet')
        ) {
          return null
        }

        if (
          (isIOS && action === 'googleWallet') ||
          (!isIOS && action === 'appleWallet')
        ) {
          return null
        }
        if (isAndroid !== isIOS && action === 'addToWallet') {
          return null
        }
        if (
          !isIOS &&
          !isAndroid &&
          (action === 'appleWallet' || action === 'googleWallet') &&
          parentAction !== 'addWallet'
        ) {
          return null
        }

        if (isLoading) {
          return (
            <BaseView
              key={action}
              style={{
                height: '105px',
                backgroundColor: theme.colors.background.secondaryGray,
                borderRadius: '24px',
              }}
              className="animate-pulse"
            />
          )
        }

        return (
          <CardItem.Root
            key={action}
            style={{
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: `${responsiveAdapter(12)}px`,
              padding: `${responsiveAdapter(16)}px`,
              minHeight: '105px',
              opacity: isDisabled || isLoadingLockAndUnlock ? 0.6 : 1,
              cursor:
                isDisabled || isLoadingLockAndUnlock
                  ? 'not-allowed'
                  : 'pointer',
              pointerEvents:
                isDisabled || isLoadingLockAndUnlock ? 'none' : 'auto',
            }}
            disabled={isDisabled || isLoadingLockAndUnlock}
            onPress={() => handleActions(action)}
          >
            <BaseView
              style={{
                width: `${responsiveAdapter(44)}px`,
                height: `${responsiveAdapter(44)}px`,
                borderRadius: `${responsiveAdapter(22)}px`,
                backgroundColor: theme.colors.background.secondaryGray,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isLoadingLockAndUnlock ? (
                <BaseView
                  style={{
                    width: '24px',
                    height: '24px',
                    border: '2px solid transparent',
                    borderTop: `2px solid ${theme.colors.label.default}`,
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              ) : (
                <AppIcons name={iconName} size={24} />
              )}
            </BaseView>

            <BaseText
              fontSize={responsiveAdapter(16)}
              fontFamily="medium"
              color={
                isDisabled || isLoadingLockAndUnlock
                  ? 'label.disabled'
                  : 'label.default'
              }
              textAlign="center"
            >
              {t(translationKey)}
            </BaseText>
          </CardItem.Root>
        )
      },
    )
  }

  return (
    <>
      <BaseView
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: `${responsiveAdapter(12)}px`,
          width: '100%',
        }}
      >
        {actions(kemCardActions({ isLocked }))}
      </BaseView>
      <Modal
        isOpen={isManageCard}
        onClose={() => setIsManageCard(false)}
        useBottomOffset={true}
      >
        <BaseView style={{ gap: `${responsiveAdapter(12)}px` }}>
          {actions(kemCardManageActions({ isLocked }))}
        </BaseView>
      </Modal>
      <Modal isOpen={isAddToWallet} onClose={() => setIsAddToWallet(false)}>
        <BaseView style={{ gap: `${responsiveAdapter(12)}px` }}>
          {actions(addToWalletActions({ isLocked }))}
        </BaseView>
      </Modal>
    </>
  )
}
